// PARAQTA — PWA орнату (платформаға сезімтал) + service worker.
// Орта анықтайды: орнатылған / Android-Chrome (бір түрту) / iOS Safari (нұсқау) /
// Telegram браузер (алдымен браузерде аш). Жақсы сәтте, мазаламай, бір рет ұсынады.

import { track } from '../core/track.js';
import { claim, release, afterGate, claimSecondary } from '../core/ui-queue.js';

let _deferred = null;     // beforeinstallprompt (Android/Chrome/desktop)
let _styled = false;
let _opens = 0;

const UA = () => navigator.userAgent || '';
const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true ||
  document.referrer.startsWith('android-app://');
const isIOS = () =>
  /iPhone|iPad|iPod/i.test(UA()) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/i.test(UA());
const isTelegram = () =>
  /Telegram/i.test(UA()) || !!window.TelegramWebviewProxy ||
  !!(window.Telegram && window.Telegram.WebView);

const LS = {
  get(k) { try { return localStorage.getItem(k); } catch (_) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (_) {} },
  num(k) { const n = Number(this.get(k)); return isNaN(n) ? 0 : n; },
};
const K_DISMISS = 'pq_pwa_dismiss';      // соңғы жабу (ms)
const K_COUNT = 'pq_pwa_dismisscount';   // неше рет жабылды
const K_INSTALLED = 'pq_pwa_installed';

// ── ЖАҢА НҰСҚАҒА АВТОМАТТЫ КӨШУ ───────────────────────────────────────
// Мәселе (2026-08-15 табылды): деплойдан кейін орнатылған қосымша (PWA) ЕСКІ
// кодта қала береді. Service worker жаңарса да, ашық тұрған беттің JS-і жадта
// сол күйінде — адам қосымшаны толық жаппайынша жаңа мүмкіндіктерді КӨРМЕЙДІ.
// (Founder: «сайтта шықты, PWA-да шықпады» — бүкіл қолданушыға қатысты.)
//
// Шешім: жаңа SW басқаруға көшкенде бетті БІР рет қайта жүктейміз.
// Қауіпсіздік шарттары:
//   · тек бұрын контроллер БОЛҒАН болса (алғаш орнатуда қайта жүктемейміз);
//   · оқу сессиясы жүріп жатса — ҚОЛ ТИМЕЙМІЗ, адам қайта кіргенде жаңарады;
//   · бір бет өмірінде бір-ақ рет (цикл болмасын).
function initAutoUpdate() {
  const sw = navigator.serviceWorker;
  if (!sw) return;
  // Браузер жаңа sw.js бар-жоғын өз бетінше СИРЕК тексереді (≈тәулігіне бір).
  // Қосымша ашылған/қайта фокусқа келген сайын тексеруді өзіміз сұраймыз —
  // әйтпесе орнатылған қосымша жаңартуды ұзақ көрмей жүреді.
  let lastCheck = 0;
  const checkUpdate = () => {
    if (Date.now() - lastCheck < 15 * 60 * 1000) return;   // 15 мин — жиілік шегі
    lastCheck = Date.now();
    sw.getRegistration().then(r => { if (r) r.update().catch(() => {}); }).catch(() => {});
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkUpdate();
  });
  setTimeout(checkUpdate, 4000);

  if (!sw.controller) return;   // алғаш орнату — қайта жүктеудің қажеті жоқ
  let done = false, pending = false;

  const busy = () => {
    try { return !!window.__pqSessionActive; } catch (_) { return false; }
  };
  const doReload = () => {
    if (done) return;
    if (busy()) { pending = true; return; }   // оқып отыр — кейінге
    done = true;
    try { location.reload(); } catch (_) {}
  };

  sw.addEventListener('controllerchange', doReload);
  // Сессия біткен соң / қосымшаға қайта кіргенде кейінге қалғанын орындаймыз
  document.addEventListener('visibilitychange', () => {
    if (pending && document.visibilityState === 'visible') doReload();
  });
}

export function initPWA() {
  // Telegram Mini App ішінде PWA орнату мағынасыз (Telegram-да қазірдің өзінде
  // бар) әрі шатастырады → толық өткіземіз.
  try { if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData) return; } catch (_) {}
  if ('serviceWorker' in navigator) {
    try { navigator.serviceWorker.register('/sw.js'); } catch (_) {}
    initAutoUpdate();
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferred = e;
  });
  window.addEventListener('appinstalled', () => {
    LS.set(K_INSTALLED, '1');
    try { track('pwa_installed'); } catch (_) {}
    closeSheet();
  });
  _opens = LS.num('pq_pwa_opens') + 1;
  LS.set('pq_pwa_opens', String(_opens));
  maybeAutoPrompt();
}

function eligible() {
  return !isStandalone() && !LS.get(K_INSTALLED);
}

function maybeAutoPrompt() {
  if (!eligible()) return;
  if (_opens < 3) return;                    // алғашқы 2 кіруде мазаламаймыз
  if (LS.num(K_COUNT) >= 3) return;          // 3 реттен соң тоқта (мәзірден қол жетімді)
  const last = LS.num(K_DISMISS);
  if (last && Date.now() - last < 3 * 24 * 3600 * 1000) return; // 3 күн күт
  // БҰРЫН: 4.5с таймер — онбординг ашық тұрса, оның ҮСТІНЕ шығатын.
  // ЕНДІ: онбординг шешімін күтеміз, сосын кезекке тұрамыз. Онбординг
  // ашық болса — ол жабылғанда ғана шығады.
  afterGate(() => {
    setTimeout(() => {
      if (!eligible() || document.getElementById('pqPwaOv')) return;
      // 22.09: бірінші оқу сессиясынан кейін, тәулігіне бір терезе.
      claimSecondary('pwa', () => openInstall('auto'));
    }, 3000);
  });
}

// Тұрақты кіру нүктесі (профиль/мәзір батырмасы үшін) + ескі data-action үйлесімі
export function installPWA() { openInstall('manual'); }
export function openInstallPrompt() { openInstall('manual'); }

const SHARE_ICON = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-3px;"><path d="M12 16V4"/><path d="M8 8l4-4 4 4"/><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7"/></svg>`;
const DOTS_ICON = `<svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" style="vertical-align:-3px;"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>`;

function ensureStyle() {
  if (_styled) return; _styled = true;
  const st = document.createElement('style');
  st.textContent = `
  @keyframes pqPwaUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
  @keyframes pqPwaFade{from{opacity:0}to{opacity:1}}
  #pqPwaOv{position:fixed;inset:0;z-index:9200;display:flex;align-items:flex-end;justify-content:center;
    background:rgba(15,13,10,.55);animation:pqPwaFade .2s ease;}
  .pq-pwa{width:100%;max-width:440px;background:var(--card);border-radius:22px 22px 0 0;
    padding:10px 22px 26px;box-shadow:0 -10px 40px rgba(0,0,0,.3);animation:pqPwaUp .3s cubic-bezier(.2,.8,.3,1);
    box-sizing:border-box;}
  .pq-pwa-grip{width:40px;height:4px;border-radius:3px;background:var(--bg4);margin:0 auto 16px;}
  .pq-pwa-top{display:flex;align-items:center;gap:13px;margin-bottom:14px;}
  .pq-pwa-ico{width:54px;height:54px;border-radius:13px;box-shadow:0 3px 10px rgba(0,0,0,.18);}
  .pq-pwa-title{font-family:var(--font-b,inherit);font-size:16px;font-weight:700;color:var(--ink);line-height:1.25;}
  .pq-pwa-sub{font-size:12px;color:var(--ink3);margin-top:3px;line-height:1.4;}
  .pq-pwa-steps{background:var(--bg2);border-radius:13px;padding:13px 15px;margin-bottom:16px;}
  .pq-pwa-step{display:flex;gap:9px;align-items:flex-start;font-size:13px;color:var(--ink2);line-height:1.5;}
  .pq-pwa-step+.pq-pwa-step{margin-top:9px;}
  .pq-pwa-n{flex:0 0 19px;height:19px;border-radius:50%;background:var(--ac);color:var(--bg);font-size:11px;
    font-weight:700;display:flex;align-items:center;justify-content:center;margin-top:1px;}
  .pq-pwa-tg{font-size:12px;color:var(--ink3);background:var(--acl);border-radius:10px;padding:9px 12px;margin-bottom:14px;line-height:1.5;}
  .pq-pwa-btns{display:flex;flex-direction:column;gap:9px;}
  .pq-pwa-btn{width:100%;padding:13px;border-radius:999px;font-size:14px;font-weight:700;cursor:pointer;border:none;font-family:var(--font-b,inherit);}
  .pq-pwa-btn.primary{background:var(--ac);color:var(--bg);}
  .pq-pwa-btn.ghost{background:transparent;color:var(--ink3);border:1.5px solid var(--bg4);}`;
  document.head.appendChild(st);
}

function closeSheet() {
  const ov = document.getElementById('pqPwaOv');
  if (ov) ov.remove();
  release('pwa');                  // кезектегі келесісіне жол ашылады
}

function dismiss() {
  LS.set(K_DISMISS, String(Date.now()));
  LS.set(K_COUNT, String(LS.num(K_COUNT) + 1));
  closeSheet();
}

function openInstall(source) {
  if (isStandalone()) { return; }
  if (document.getElementById('pqPwaOv')) return;
  ensureStyle();
  try { track('pwa_prompt', { src: source, plat: isIOS() ? 'ios' : isAndroid() ? 'android' : 'other', d: _deferred ? 1 : 0 }); } catch (_) {}

  // Орта бойынша дене
  let stepsHtml = '';
  let primaryLabel = 'Түсіндім';
  let primaryAct = 'ok';
  let tgHint = '';

  if (_deferred) {
    // Android Chrome / Desktop — бір түрту
    stepsHtml = `<div class="pq-pwa-steps"><div class="pq-pwa-step"><span class="pq-pwa-n">✓</span><span>Бір түртумен орнатылады — төмендегі «Орнату» түймесін бас.</span></div></div>`;
    primaryLabel = 'Орнату';
    primaryAct = 'install';
  } else if (isIOS()) {
    // iOS Safari — қолмен нұсқау
    stepsHtml = `<div class="pq-pwa-steps">
      <div class="pq-pwa-step"><span class="pq-pwa-n">1</span><span>Төмендегі ${SHARE_ICON} <b>Бөлісу</b> түймесін бас</span></div>
      <div class="pq-pwa-step"><span class="pq-pwa-n">2</span><span><b>«Негізгі экранға қосу»</b> (Add to Home Screen) таңда</span></div>
      <div class="pq-pwa-step"><span class="pq-pwa-n">3</span><span><b>«Қосу»</b> басып растай ғой</span></div>
    </div>`;
    if (isTelegram()) tgHint = `Telegram ішінде тұрсың — егер ${SHARE_ICON} көрінбесе, төмендегі ••• → <b>«Safari-де ашу»</b>, сосын осы қадамдарды қайтала.`;
  } else {
    // Android (Telegram/басқа webview) немесе белгісіз — браузерде аш
    stepsHtml = `<div class="pq-pwa-steps">
      <div class="pq-pwa-step"><span class="pq-pwa-n">1</span><span>Браузер мәзірін аш ${DOTS_ICON}</span></div>
      <div class="pq-pwa-step"><span class="pq-pwa-n">2</span><span><b>«Қолданбаны орнату»</b> немесе <b>«Негізгі экранға қосу»</b> таңда</span></div>
    </div>`;
    if (isTelegram()) tgHint = `Telegram ішінде ашып тұрсың — алдымен ${DOTS_ICON} → <b>«Браузерде ашу»</b> (Chrome), сосын орнат.`;
  }

  const ov = document.createElement('div');
  ov.id = 'pqPwaOv';
  ov.innerHTML = `
    <div class="pq-pwa" role="dialog" aria-label="Қолданбаны орнату">
      <div class="pq-pwa-grip"></div>
      <div class="pq-pwa-top">
        <img class="pq-pwa-ico" src="/icon-192-v5.png" alt="PARAQTA"/>
        <div>
          <div class="pq-pwa-title">PARAQTA-ны негізгі экранға қос</div>
          <div class="pq-pwa-sub">Жылдам ашылады · хабарламалар келеді · браузер жолағынсыз, нағыз қолданбадай</div>
        </div>
      </div>
      ${stepsHtml}
      ${tgHint ? `<div class="pq-pwa-tg">${tgHint}</div>` : ''}
      <div class="pq-pwa-btns">
        <button class="pq-pwa-btn primary" data-pwa="${primaryAct}">${primaryLabel}</button>
        <button class="pq-pwa-btn ghost" data-pwa="later">Кейін</button>
      </div>
    </div>`;
  document.body.appendChild(ov);

  ov.addEventListener('click', (e) => {
    const t = e.target.closest('[data-pwa]');
    const act = t && t.dataset.pwa;
    if (act === 'install') {
      try { track('pwa_install_click'); } catch (_) {}
      if (_deferred) {
        _deferred.prompt();
        _deferred.userChoice.then((r) => {
          try { track('pwa_choice', { o: r && r.outcome }); } catch (_) {}
          _deferred = null;
          closeSheet();
        }).catch(() => closeSheet());
      } else { closeSheet(); }
      return;
    }
    if (act === 'ok') { dismiss(); return; }        // нұсқауды оқыды
    if (act === 'later') { dismiss(); return; }
    if (e.target === ov) { dismiss(); }             // фонға басу
  });
}
